import { Duration } from 'luxon';

import {
  AllDaysOfWeek, AllMonthsOfYear, AllTimesOfDay,
  DAY_PUBLIC_HOLIDAY, Days, Months,
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { timeOfDayToDuration } from 'src/util/timeOfUse';

/**
 * Extracts price from community trade rules based on meter data and trade direction.
 *
 * This function searches through the meter's community rules to find a matching rule
 * for the specified trade direction (buy/sell) and returns the price from the first
 * clause if exactly one rule and one clause are found. Returns empty string for
 * invalid inputs or when no single matching rule/clause combination exists.
 * @param {object} meter - The meter object containing trade point ID and community rules
 * @param {object} meter.tradePointId - UUID of the trade point associated with the meter
 * @param {object} meter.communityRules - Community rules connection from GraphQL
 * @param {Array} meter.communityRules.edges - Array of rule edges containing rule nodes
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} direction - The trade direction constant
 * @returns {string} The price string from the matching clause, or empty string if no valid match
 */
export const getPrice = (meter, direction) => {
  let price = '';
  if (!meter || !direction) {
    return price;
  }

  const { tradePointId, communityRules } = meter;

  // Handle malformed or missing communityRules
  if (!communityRules || !communityRules.edges || !Array.isArray(communityRules.edges)) {
    return price;
  }

  // Handle missing tradePointId
  if (!tradePointId) {
    return price;
  }

  const currentRules = communityRules.edges.filter((edge) => {
    if (direction === TRADE_DIRECTION_BUY) {
      return edge?.node?.buyer?.tradePoint?.id === tradePointId;
    }
    if (direction === TRADE_DIRECTION_SELL) {
      return edge?.node?.seller?.tradePoint?.id === tradePointId;
    }
    return false;
  });

  if (currentRules.length === 1) {
    const filteredRules = currentRules[0].node.clauses.edges;
    if (filteredRules.length === 1) {
      price = filteredRules[0].node.price;
    }
  }

  return price;
};

/**
 * Get formatted clauses for a specific trade direction from community rules.
 * @param {object} meter
 * @param {string} direction - The trade direction ('buy' or 'sell')
 * @param {Function} formatClauseFromEdge - Function to format clause from edge
 * @param {TRADE_TYPE_COMMUNITY | TRADE_TYPE_RESIDUAL} tradeType
 * @returns {Array} Array of formatted clauses for the specified direction
 */
export const getClauses = (meter, direction, formatClauseFromEdge, tradeType) => {
  if (!meter || !direction || !formatClauseFromEdge) {
    return [];
  }

  const { tradePointId, communityRules, residualRules } = meter;

  const rules = tradeType === TRADE_TYPE_COMMUNITY ? communityRules : residualRules;

  const currentRules = rules?.edges?.filter((edge) => {
    if (direction === TRADE_DIRECTION_BUY) {
      return edge?.node?.buyer?.tradePoint?.id === tradePointId;
    }
    if (direction === TRADE_DIRECTION_SELL) {
      return edge?.node?.seller?.tradePoint?.id === tradePointId;
    }
    return false;
  });

  if (currentRules?.length === 1) {
    return currentRules[0].node.clauses.edges.map(formatClauseFromEdge);
  }

  return [];
};

/**
 * Builds normalized time combinations from trade rule clauses for validation.
 * Converts clause month/day/time specifications into sortable combination objects.
 * @param {Array<object>} clauses - Array of trade rule clause objects
 * @param {boolean} ignorePublicHolidays - Whether to exclude public holidays
 * @returns {Array<object>} Array of combination objects with month, day, and time properties
 */
export const buildTimeCombinations = (clauses, ignorePublicHolidays) => {
  const relevantDays = ignorePublicHolidays ? Days.slice(0, 7) : Days;
  const combinations = [];

  clauses.forEach(({ monthsOfYear, daysOfWeek, timesOfDay }) => {
    monthsOfYear.forEach((month) => {
      daysOfWeek.forEach((day) => {
        if (ignorePublicHolidays && day === DAY_PUBLIC_HOLIDAY) {
          return;
        }
        timesOfDay.forEach(({ start, finish }) => {
          combinations.push({
            month: Months.indexOf(month), // 0-11 (Jan-Dec)
            day: relevantDays.indexOf(day), // 0-6 (Mon-Sun) or 0-7 (with holidays)
            time: {
              start: timeOfDayToDuration(start).valueOf(),
              finish: timeOfDayToDuration(finish).valueOf(),
            },
          });
        });
      });
    });
  });

  return combinations;
};

/**
 * Sorts time combinations chronologically by month, day, start time, then finish time.
 * @param {Array<object>} combinations - Array of time combination objects
 * @returns {Array<object>} Sorted array of combinations
 */
export const getSortedCombinations = (combinations) => [...combinations].sort((a, b) => (
  a.month - b.month
    || a.day - b.day
    || a.time.start - b.time.start
    || a.time.finish - b.time.finish
));

/**
 * Validates that time combinations provide complete year coverage from start to finish.
 * Checks that coverage begins at January 1st 00:00:00 and ends at December 31st 24:00:00.
 * @param {Array<object>} combinations - Sorted array of time combinations
 * @param {Array<string>} relevantDays - Array of relevant day constants
 * @returns {boolean} True if combinations cover complete year boundaries
 */
export const getYearBoundaryValidation = (combinations, relevantDays) => {
  if (!combinations || !Array.isArray(combinations) || combinations.length === 0) {
    return false;
  }

  if (!relevantDays || !Array.isArray(relevantDays) || relevantDays.length === 0) {
    return false;
  }

  const endOfDay = Duration.fromObject({ hours: 24 }).valueOf();
  const first = combinations[0];
  const last = combinations[combinations.length - 1];

  // Must start at beginning of year: January (0), first day (0), 00:00:00 (0)
  const startsAtYearBeginning = (
    first.month === 0
    && first.day === 0
    && first.time.start === 0
  );

  // Must end at end of year: December (11), last day, 24:00:00
  const endsAtYearEnd = (
    last.month === Months.length - 1
    && last.day === relevantDays.length - 1
    && last.time.finish === endOfDay
  );

  return startsAtYearBeginning && endsAtYearEnd;
};

/**
 * Builds the expected next time combination based on temporal progression rules.
 * Handles day/month transitions when previous combination ends at end of day.
 * @param {object} prevCombination - Previous combination object
 * @param {Array<string>} relevantDays - Array of relevant day constants
 * @returns {object} Expected next combination with month, day, and start time
 */
export const buildExpectedNextCombination = (prevCombination, relevantDays) => {
  if (!prevCombination) {
    return {
      month: undefined,
      day: undefined,
      start: undefined,
    };
  }

  const endOfDay = Duration.fromObject({ hours: 24 }).valueOf();

  let expectedMonth = prevCombination.month;
  let expectedDay = prevCombination.day;
  let expectedStart = prevCombination.time.finish;

  if (prevCombination.time.finish === endOfDay) {
    expectedStart = 0;
    if (prevCombination.day === relevantDays.length - 1) {
      expectedDay = 0;
      expectedMonth = prevCombination.month + 1;
    } else {
      expectedDay = prevCombination.day + 1;
    }
  }

  return {
    month: expectedMonth,
    day: expectedDay,
    start: expectedStart,
  };
};

/**
 * Validates temporal consistency of combinations, checking for overlaps and gaps.
 * Returns validation status and coverage completeness.
 * @param {Array<object>} combinations - Sorted array of time combinations
 * @param {Array<string>} relevantDays - Array of relevant day constants
 * @returns {{valid: boolean, coverage: boolean}} Validation result
 */
export const getTemporalValidation = (combinations, relevantDays) => {
  let coverage = true;

  for (let i = 1; i < combinations.length; i += 1) {
    const curr = combinations[i];
    const prev = combinations[i - 1];

    const expected = buildExpectedNextCombination(prev, relevantDays);

    const comparison = (
      curr.month - expected.month
      || curr.day - expected.day
      || curr.time.start - expected.start
    );

    if (comparison < 0) {
      return { valid: false }; // Overlap detected - return only valid property
    }
    if (comparison > 0) {
      coverage = false; // Gap detected
    }
  }

  return { valid: true, coverage };
};

/**
 * Validates trade rule clauses for temporal consistency and coverage completeness.
 *
 * This function performs comprehensive validation of trade rule clauses by checking for:
 * 1. Temporal validity - ensures no overlapping time periods within clauses
 * 2. Coverage completeness - verifies clauses provide full year coverage (365 days, 24 hours)
 *
 * The validation process converts clause time specifications into normalized combinations
 * of month/day/time tuples, sorts them chronologically, and checks for gaps or overlaps.
 * Full coverage requires continuous time periods from January 1st 00:00:00 through
 * December 31st 24:00:00 (or excluding public holidays if specified).
 *
 * Each clause object should contain:
 * - monthsOfYear: Array of month enum strings (JANUARY, FEBRUARY, etc.)
 * - daysOfWeek: Array of day enum strings (MONDAY, TUESDAY, etc.)
 * - timesOfDay: Array of time span objects with start/finish time objects
 * - start/finish: Objects with hours, minutes, seconds numeric properties
 * @param {Array<object>} clauses - Array of trade rule clause objects from GraphQL Clause type
 * @param {boolean} ignorePublicHolidays - Whether to exclude PUBLIC_HOLIDAY from coverage
 * validation
 * @returns {{valid: boolean, coverage: boolean}} Validation result object with two properties:
 *   - valid: True if clauses have no temporal overlaps, false otherwise
 *   - coverage: True if clauses provide complete time coverage, false for gaps
 * @example
 * // Validate clauses with full year coverage
 * const clauses = [{
 *   monthsOfYear: ['JANUARY', 'FEBRUARY', 'MARCH'],
 *   daysOfWeek: ['MONDAY', 'TUESDAY', 'WEDNESDAY'],
 *   timesOfDay: [{
 *     start: { hours: 0, minutes: 0, seconds: 0 },
 *     finish: { hours: 24, minutes: 0, seconds: 0 }
 *   }]
 * }];
 * const result = validateClauses(clauses, false);
 * // Returns: { valid: true, coverage: false } (partial coverage - only 3 months)
 */
export const validateClauses = (clauses, ignorePublicHolidays) => {
  if (!Array.isArray(clauses)) {
    return { valid: false, coverage: false };
  }

  const combinations = buildTimeCombinations(clauses, ignorePublicHolidays);

  if (combinations.length === 0) {
    return { valid: true, coverage: false };
  }

  const sortedCombinations = getSortedCombinations(combinations);
  const relevantDays = ignorePublicHolidays ? Days.slice(0, 7) : Days;

  const hasYearBoundaries = getYearBoundaryValidation(sortedCombinations, relevantDays);
  const temporalResult = getTemporalValidation(sortedCombinations, relevantDays);

  // If temporal validation found overlaps, return only {valid: false}
  if (!temporalResult.valid) {
    return { valid: false };
  }

  return {
    valid: true,
    coverage: temporalResult.coverage && hasYearBoundaries,
  };
};

/**
 * Builds a clause input array for community trade rule proposals with full temporal coverage.
 * The generated clause is designed for flat pricing scenarios where a single price applies
 * across all time periods.
 * @param {number|string|null} price - The energy price for the clause.
 * @param {string} timezone
 * @param {string|null} publicHolidayRegion
 * @returns {Array<object>} Array containing a single clause input object structured for
 *   GraphQL InputClause type.
 */
export const buildClauseInput = (price, timezone, publicHolidayRegion) => [{
  price,
  timezone,
  publicHolidayRegion,
  ignoreDaylightSavings: false,
  ignorePublicHolidays: false,
  monthsOfYear: AllMonthsOfYear,
  daysOfWeek: AllDaysOfWeek,
  timesOfDay: [AllTimesOfDay],
}];
